
// Helper to get page title from route
// Titles are matched against the real /app/* routes used by the app router.
// Falls back to a Title-Cased version of the last path segment.
export const getPageTitle = (pathname: string): string => {
  const titles: Record<string, string> = {
    '/': 'Dashboard',
    '/app/dashboard': 'Admin Dashboard',
    '/app/home': 'Dashboard',
    '/app/performance': 'Performance Metrics',
    '/app/projects': 'Project Management',
    '/app/schedule': 'Schedule',
    '/app/settings': 'Account Settings',
    '/app/settings/rbac': 'Access Control',
    '/app/chat': 'Chat',
    '/app/system-health': 'System Health',
    '/app/coder': 'AI Code Review',
    '/app/ai-lead-search': 'AI Lead Search',
    '/app/leads': 'Lead Management',
    '/app/leads/add': 'Add Lead',
    '/app/leads/new': 'Add Lead',
    '/app/leads/find': 'Find Leads',
    '/app/leads/contacts': 'Contacts',
    '/app/leads/contacts/new': 'Add Contact',
    '/app/leads/analytics': 'Lead Analytics',
    '/app/leads/import': 'Import Leads',
    '/app/leads/assignment': 'Lead Assignment',
    '/app/leads/pipeline': 'Sales Pipeline',
    '/app/dialer': 'Voice Dialer',
    '/app/phone': 'Voice Dialer',
    '/app/campaigns': 'Campaign Manager',
    '/app/campaigns/new': 'Create Campaign',
    '/app/campaigns/edit': 'Edit Campaign',
    '/app/customers/reengage': 'Re-Engage Customers',
    '/app/customers/finance': 'Finance Overview',
    '/app/customers/dues': 'Due Report',
    '/app/customers/collections': 'Collections',
    '/app/customers/aging': 'A/R Aging',
    '/app/customers/unsent': 'Unsent Invoices',
    '/app/customers/alerts': 'Finance Alerts',
    '/app/ivr': 'IVR Management',
    '/app/ivr/numbers': 'Phone Numbers',
    '/app/ivr/menus': 'IVR Menus',
    '/app/ivr/menus/new': 'Create IVR Menu',
    '/app/ivr/routing': 'Call Routing',
    '/app/ivr/call-logs': 'Call Logs',
    '/app/ivr/error-logs': 'Error Logs',
    '/app/ivr/debug': 'Twilio Debug Tools',
    '/app/ivr/log-sync': 'IVR Log Sync',
    '/app/ivr/mock-calls': 'Mock Calls',
    '/app/agents': 'Agent Builder',
    '/app/agent': 'Agent Builder',
    '/app/agent/add': 'Add Agent',
    '/app/agent/add-elevenlabs': 'Add ElevenLabs Agent',
    '/app/agent-sessions': 'Agent Sessions',
    '/app/agent-debug': 'Agent Debug',
    '/app/agent-tools': 'Agent Tools',
    '/app/agent-workflow': 'Agent Workflows',
    '/app/agent-workflow-visual': 'Visual Workflow Builder',
    '/app/agent-log': 'Agent Log',
    '/app/agent-graph': 'Agent Graph',
    '/app/training': 'Agent Training',
    '/app/memory': 'Agent Memory',
    '/app/knowledge': 'Knowledge Base',
    '/app/knowledge-base': 'Knowledge Base',
    '/app/agent/knowledge-base': 'Knowledge Base',
    '/app/templates': 'Templates',
    '/app/reports/calls': 'Call Report',
    '/app/reports/all-activity': 'All User Activity',
    '/app/reports/system-log': 'System Log',
    '/app/reports/documentation': 'Documentation',
    '/app/user-activity': 'User Activity',
    '/app/help': 'Help',
    '/app/access-control': 'Access Control',
    '/app/access-control/dashboard': 'Access Control',
    '/app/access-control/permissions': 'Permission Matrix',
    '/app/access-control/users': 'User Management',
    '/app/access-control/roles': 'Role Management',
    '/app/access-control/resources': 'Resource Management',
    '/app/profile': 'Profile',
    '/app/my/activity': 'My Activity',
    '/app/proposals': 'Proposals',
    '/app/services': 'Services',
    '/app/contacts': 'Contacts',
    // legacy / non-prefixed
    '/dashboard': 'Dashboard',
    '/performance': 'Performance Metrics',
    '/projects': 'Project Management',
    '/schedule': 'Schedule',
    '/team': 'Team Members',
    '/settings': 'Account Settings',
    '/chat': 'Chat',
    '/system-health': 'System Health',
    '/seo-audit': 'SEO Audit Tool',
    '/coder': 'AI Code Review',
    '/ai-lead-search': 'AI Lead Search',
    '/leads': 'Lead Management',
    '/leads/find': 'Find Leads',
    '/dialer': 'Voice Dialer',
    '/phone': 'Voice Dialer',
    '/campaigns': 'Campaign Manager',
    '/ivr': 'IVR Management',
    '/agents': 'Agent Builder',
    '/agent': 'Agent Builder',
    '/agent-sessions': 'Agent Sessions',
    '/agent-debug': 'Agent Debug',
    '/agent-tools': 'Agent Tools',
    '/agent-workflow': 'Agent Workflows',
    '/agent-log': 'Agent Log',
    '/agent-graph': 'Agent Graph',
    '/training': 'Agent Training',
    '/knowledge': 'Knowledge Base',
    '/knowledge-base': 'Knowledge Base',
    '/templates': 'Templates',
    '/reports': 'Reports',
    '/reports/calls': 'Call Report',
    '/reports/all-activity': 'All User Activity',
    '/reports/system-log': 'System Log',
    '/reports/documentation': 'Documentation',
    '/user-activity': 'User Activity',
    '/help': 'Help',
    '/access-control': 'Access Control',
    '/profile': 'Profile',
    '/proposals': 'Proposals',
    '/services': 'Services',
    '/contacts': 'Contacts',
  };

  if (titles[pathname]) return titles[pathname];

  // Dynamic /app/* fallbacks
  if (pathname.startsWith('/app/leads/') || pathname.startsWith('/leads/')) {
    if (pathname.includes('/timeline')) return 'Activity Timeline';
    if (pathname.includes('/edit')) return 'Edit Lead';
    return 'Lead Details';
  }
  if (pathname.startsWith('/app/campaigns/edit/') || pathname.startsWith('/campaigns/edit/')) {
    return 'Edit Campaign';
  }
  if (pathname.startsWith('/app/ivr/menus/') || pathname.startsWith('/ivr/menus/')) {
    return 'Edit IVR Menu';
  }
  if (pathname.startsWith('/app/agent/') || pathname.startsWith('/agent/')) {
    if (pathname.includes('/edit/')) return 'Edit Agent';
    return 'Agent Builder';
  }
  if (pathname.startsWith('/app/my/activity/') || pathname.startsWith('/my/activity/')) {
    return 'My Activity';
  }
  if (pathname.startsWith('/app/proposals/create/') || pathname.startsWith('/proposals/create/')) {
    return 'Create Proposal';
  }
  if (pathname.startsWith('/app/contacts/') || pathname.startsWith('/contacts/')) {
    return 'Contact Details';
  }
  if (pathname.startsWith('/app/email-templates') || pathname.startsWith('/email-templates')) {
    return 'Email Templates';
  }

  // /app/email/:folder[/:emailId] — show the folder name; the detail view
  // overrides the title with the email subject via the dynamic title store.
  const emailMatch = pathname.match(/^\/(?:app\/)?email(?:\/([^/]+))?/);
  if (emailMatch) {
    const folder = emailMatch[1];
    if (!folder) return 'Email';
    const folderTitles: Record<string, string> = {
      inbox: 'Inbox',
      sent: 'Sent',
      drafts: 'Drafts',
      trash: 'Trash',
      archive: 'Archive',
      spam: 'Spam',
      junk: 'Junk',
      starred: 'Starred',
    };
    return (
      folderTitles[folder.toLowerCase()] ||
      folder.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    );
  }

  // Fallback: title-case the last meaningful segment
  const segments = pathname.split('/').filter(Boolean).filter(s => s !== 'app');
  const last = segments[segments.length - 1];
  if (!last) return 'Dashboard';
  return last
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};
